import { isoAttr, formatTimeOfDay } from "../../lib/datetime.ts";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast, type RoleState } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import { useDataQuality } from "../../state/data-quality.tsx";
import { useSystemHealth, type SystemWindow } from "./use-system.ts";
import { QueueAcceptance } from "./QueueAcceptance.tsx";
import { StateGatewayPanel } from "./StateGatewayPanel.tsx";
import { RuntimeHealth } from "./RuntimeHealth.tsx";
import { ProcessingFailures } from "./ProcessingFailures.tsx";
import {
  readErrorMessage,
  containerLevel,
  containerStateLabel,
  gatewayLevel,
  gatewayStateLabel,
  quantityMeasure,
  formatCount,
  formatRate,
  formatAgeSeconds,
  type SystemHealth,
} from "./vocab.ts";
import { type Measure, type StatusLevel } from "../../components/index.ts";
import {
  DashboardGrid,
  KpiCard,
  StatusGrid,
  type StatusGridItem,
} from "../../components/observability/index.ts";
import "./system.css";

/**
 * The System health screen (task 11.15; the LAST Phase-T9 data screen). It renders the control-plane
 * health row design 12.2 describes over ONE REST snapshot, `GET /api/v1/system/health` (role
 * `viewer`), composed into FOUR distinct regions:
 *
 * 1. QUEUE ACCEPTANCE — container state, queue depth and age, dead-letter depth, ingest rates and
 *    drops: "is telemetry being accepted and moving?". The durable `202` side of ingest.
 * 2. STATE GATEWAY — open vs temporarily-closed, surfaced honestly (temporarily-closed is a valid
 *    degraded state, not a failure and not green; design 6.4.1).
 * 3. RUNTIME HEALTH — shard latency p50/p95, hub connections, verification lag.
 * 4. PROCESSING FAILURES — a SEPARATE region, per (source, instance, batch sequence), with attempt
 *    count, next-retry and terminal dead-letter state.
 *
 * The CRITICAL separation (design 5.3 / 6.1, FR-14.6 / FR-14.7): queue ACCEPTANCE (region 1) and
 * per-batch PROCESSING FAILURES (region 4) are two SEPARATE regions and are NEVER merged into a
 * single success/fail number — a batch&rsquo;s `202` acceptance is independent of whether it later
 * processed, and the screen keeps them physically and semantically apart.
 *
 * The snapshot is WINDOWED and respects pause/time-range (pausing freezes the query). The screen
 * handles every state design 12.3 asks for (loading / empty / error / restricted / ready), defers a
 * cold-core waking error to the shell banner, and reports the snapshot&rsquo;s data-quality into the
 * shared strip so the persistent reminder stays consistent. There is no `system` live-stream topic
 * in the contract, so this is honestly a REST snapshot that refreshes on the poll — the gateway
 * state and hub connections update on each refresh, not a push.
 */
export function SystemScreen(): ReactNode {
  const { role } = useIdentity();
  const { live } = useTimeRange();

  const canRead = roleAtLeast(role, "viewer");

  // The processing-failure filter + cursor are LOCAL screen state (they narrow only the failure
  // list, per the contract); changing them re-keys the query via the hook.
  const [sourceFilter, setSourceFilter] = useState("");
  const [instanceFilter, setInstanceFilter] = useState("");
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const { query, window } = useSystemHealth({
    enabled: canRead,
    sourceId: sourceFilter.length > 0 ? sourceFilter : undefined,
    instanceId: instanceFilter.length > 0 ? instanceFilter : undefined,
    cursor,
  });

  const onFilter = useCallback((filter: { sourceId: string; instanceId: string }) => {
    setSourceFilter(filter.sourceId);
    setInstanceFilter(filter.instanceId);
    setCursor(undefined); // a new filter resets paging
  }, []);

  const nextCursor = query.data?.processing_failures.next_cursor ?? null;
  const onNextPage = useCallback(() => {
    if (nextCursor !== null) setCursor(nextCursor);
  }, [nextCursor]);
  const onResetPage = useCallback(() => setCursor(undefined), []);

  const state = resolveState(role, query);

  return (
    <ScreenScaffold
      title="Control plane health"
      subtitle="Queue acceptance (depth, age, dead-letter, ingest rates and drops) and State Gateway availability, with shard latency, hub connections and verification lag, and, kept separate, per-batch processing failures by source, instance and sequence with retry and dead-letter state. Needs the viewer role; the server enforces regardless."
      state={state}
    >
      {query.data !== undefined && (
        <SystemBody
          data={query.data}
          live={live}
          window={window}
          sourceFilter={sourceFilter}
          instanceFilter={instanceFilter}
          onFilter={onFilter}
          onNextPage={onNextPage}
          onResetPage={onResetPage}
          isPaged={cursor !== undefined}
        />
      )}
    </ScreenScaffold>
  );
}

/** Decide the scaffold state from the role and the health query. */
function resolveState(role: RoleState, query: ReturnType<typeof useSystemHealth>["query"]): ScreenState {
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so the control-plane health view is hidden until it is. The server enforces access regardless."
          : "The control-plane health view needs at least the viewer role.",
    };
  }

  if (query.data !== undefined) {
    // There is always something to show (the snapshot tiles exist even with no failures), so a
    // loaded snapshot is "ready"; the processing-failure table shows its own honest empty message.
    return { kind: "ready" };
  }

  if (query.isError) {
    if (query.error.isWaking) return { kind: "loading" }; // the shell banner owns the waking state
    return { kind: "error", message: readErrorMessage(query.error) };
  }

  return { kind: "loading" };
}

function SystemBody(props: {
  data: SystemHealth;
  live: boolean;
  window: SystemWindow;
  sourceFilter: string;
  instanceFilter: string;
  onFilter: (filter: { sourceId: string; instanceId: string }) => void;
  onNextPage: () => void;
  onResetPage: () => void;
  isPaged: boolean;
}): ReactNode {
  const { data } = props;
  const generatedAt = data.meta.generated_at;

  // Keep the persistent data-quality strip consistent with this snapshot (design 12.2, NFR-4), the
  // same way the Overview's ingest strip does. Only the fields the snapshot carries are reported.
  const { report } = useDataQuality();
  // Guard against a partial response: `meta` or `data_quality` could be absent in a stub.
  const dq = data.meta?.data_quality;
  const gapCount = (dq?.gaps ?? []).length;
  const dropsInRange = dq?.drops_in_range ?? 0;
  const samplingInEffect = dq?.sampling_in_effect ?? false;
  useEffect(() => {
    report({
      drops: { atSource: 0, atEdge: 0, inPipeline: dropsInRange },
      sampling: { active: samplingInEffect, rate: null, exact: !samplingInEffect },
      hasSequenceGap: gapCount > 0,
    });
  }, [report, dropsInRange, samplingInEffect, gapCount]);

  const windowMeta = useMemo(
    () => ({ from: props.window.fromMs, to: props.window.toMs, generated: generatedAt }),
    [props.window.fromMs, props.window.toMs, generatedAt],
  );

  return (
    <div className="sys" data-live={props.live}>
      <div className="screen-meta-row">
        <LivePauseControl />
        <TimeRangeControl />
        <p className="sys__meta">
        {props.live ? "Live" : "Paused"} · window{" "}
        <time dateTime={isoAttr(windowMeta.from)}>{formatTimeOfDay(windowMeta.from)}</time> to{" "}
        <time dateTime={isoAttr(windowMeta.to)}>{formatTimeOfDay(windowMeta.to)}</time> · generated{" "}
        <time dateTime={isoAttr(windowMeta.generated)}>{formatTimeOfDay(windowMeta.generated)}</time>
      </p>
      </div>

      {/* At-a-glance dashboard header built from the SAME health snapshot the four regions below
          render in full — a KpiCard headline row (queue acceptance + runtime) and a StatusGrid of
          the health signals (container, gateway, verification lag). The detailed regions, including
          the critical acceptance/processing-failures separation, remain unchanged below; this is a
          presentational overview, not a new read (observability-ui task 3.1). */}
      <DashboardGrid
        label="Control plane health summary"
        className="sys-dashboard"
        kpis={healthKpis(data).map((kpi) => (
          <KpiCard key={kpi.key} {...kpi.props} />
        ))}
      >
        <StatusGrid title="Health signals" items={healthSignalItems(data)} />
      </DashboardGrid>

      <QueueAcceptance
        container={data.container}
        queue={data.queue}
        deadLetter={data.dead_letter}
        ingest={data.ingest}
        ready={data.ready}
      />

      <StateGatewayPanel gateway={data.state_gateway} />

      <RuntimeHealth
        shardLatency={data.shard_latency_ms}
        hub={data.hub}
        verificationLagS={data.verification_lag_s}
      />

      <ProcessingFailures
        page={data.processing_failures}
        sourceFilter={props.sourceFilter}
        instanceFilter={props.instanceFilter}
        onFilter={props.onFilter}
        onNextPage={props.onNextPage}
        onResetPage={props.onResetPage}
        isPaged={props.isPaged}
      />
    </div>
  );
}

/** A verification lag past this many seconds is surfaced as a warn (mirrors RuntimeHealth). */
const VERIFICATION_LAG_WARN_S = 300;

/** A KpiCard plus a stable React key, so the body can map a list without inventing keys. */
interface KeyedKpi {
  readonly key: string;
  readonly props: Parameters<typeof KpiCard>[0];
}

/**
 * The headline health numbers as KpiCards (observability-ui task 3.1). Every value is read straight
 * from the SAME `SystemHealth` snapshot the four detailed regions render — nothing new is fetched
 * and nothing is fabricated. A nullable rate with no reading renders the card's honest empty state
 * (never a reassuring zero); a sampled rate carries its "est." measure. The dead-letter depth and a
 * non-zero rejected rate are surfaced as attention-needed (a `down` trend chip), never green.
 */
function healthKpis(data: SystemHealth): readonly KeyedKpi[] {
  const accepted = data.ingest.accepted_per_s;
  const rejected = data.ingest.rejected_per_s;
  const dlqDepth = data.dead_letter.depth;
  const rejectedValue = rejected.value;

  return [
    { key: "queue-depth", props: { label: "Queue depth", value: formatCount(data.queue.depth) } },
    {
      key: "accepted",
      props: rateKpi("Accepted", accepted.value, "/s", quantityMeasure(accepted)),
    },
    {
      key: "rejected",
      props: {
        ...rateKpi("Rejected", rejectedValue, "/s", quantityMeasure(rejected)),
        ...(rejectedValue !== null && rejectedValue > 0
          ? { delta: formatRate(rejectedValue), trend: "down" as const }
          : {}),
      },
    },
    {
      key: "dead-letter",
      props: {
        label: "Dead-letter depth",
        value: formatCount(dlqDepth),
        ...(dlqDepth > 0 ? { delta: `${dlqDepth}`, trend: "down" as const } : {}),
      },
    },
    { key: "hub", props: { label: "Hub connections", value: formatCount(data.hub.connections) } },
  ];
}

/**
 * Build a KpiCard for a nullable per-second rate: an honest empty state when the server returned no
 * reading, otherwise the formatted rate with its unit and exact/estimate measure.
 */
function rateKpi(
  label: string,
  value: number | null,
  unit: string,
  measure: Measure,
): Parameters<typeof KpiCard>[0] {
  if (value === null) {
    return { label, value: "n/a", empty: true, emptyHint: "No reading in this window." };
  }
  return { label, value: value.toLocaleString(undefined, { maximumFractionDigits: 2 }), unit, measure };
}

/**
 * The control-plane health signals as StatusGrid cells (observability-ui task 3.1), reusing the
 * screen's existing level/label vocabulary so each cell reads exactly as the detailed regions'
 * badges do. Container state (`ready` healthy, `waking` warn, `unavailable` error); the State
 * Gateway (open healthy, temporarily-closed an honest warn — never "down", never green, per design
 * 6.4.1); and audit-chain verification lag (current healthy, falling-behind warn, unknown grey —
 * never green when unverifiable). These are status-only cells (no click-through: the detailed
 * regions below own the drill-downs).
 */
function healthSignalItems(data: SystemHealth): readonly StatusGridItem[] {
  const lag = data.verification_lag_s;
  const lagLevel: StatusLevel =
    lag === null ? "unknown" : lag > VERIFICATION_LAG_WARN_S ? "warn" : "healthy";
  const lagLabel =
    lag === null ? "Unverifiable" : lag > VERIFICATION_LAG_WARN_S ? "Falling behind" : "Current";

  return [
    {
      id: "container",
      name: "Container",
      status: containerLevel(data.container.state),
      statusLabel: containerStateLabel(data.container.state),
      metrics: [{ label: "Ingest/reads", value: data.ready ? "Ready" : "Not ready" }],
    },
    {
      id: "state-gateway",
      name: "State Gateway",
      status: gatewayLevel(data.state_gateway.state),
      statusLabel: gatewayStateLabel(data.state_gateway.state),
      metrics: [
        {
          label: "Reason",
          value:
            data.state_gateway.reason === null || data.state_gateway.reason.length === 0
              ? "n/a"
              : data.state_gateway.reason,
        },
      ],
    },
    {
      id: "verification-lag",
      name: "Verification lag",
      status: lagLevel,
      statusLabel: lagLabel,
      metrics: [{ label: "Behind", value: lag === null ? "no data" : formatAgeSeconds(lag) }],
    },
  ];
}
